import type { MatchEventCategory } from '../../types/matchEvents.types';

/**
 * Per-category visual config for the Live Match Events tab. Mirrors
 * `notifications/notification-display.ts`:
 *   - `emoji`: a leading glyph so the operator scans the feed by shape.
 *   - `color`: hex for the row's left-border accent + the category chip
 *     outline. Themed so goals/penalties read hot, informational stats
 *     read cool.
 *   - `label`: friendly chip text.
 *
 * The `Record<MatchEventCategory, _>` shape is enforced exhaustively at
 * compile time so a new category can't be added to the enum without a
 * display entry.
 */
export interface CategoryDisplayConfig {
  emoji: string;
  color: string;
  label: string;
}

export const CATEGORY_DISPLAY: Record<
  MatchEventCategory,
  CategoryDisplayConfig
> = {
  // Hot colours for the score-changing / high-signal incidents.
  goal: { emoji: '⚽', color: '#4caf50', label: 'goal' },
  penalty: { emoji: '🎯', color: '#e91e63', label: 'penalty' },
  corner: { emoji: '🚩', color: '#03a9f4', label: 'corner' },
  card: { emoji: '🟨', color: '#fbc02d', label: 'card' },
  var: { emoji: '🖥️', color: '#9c27b0', label: 'VAR' },
  shot: { emoji: '🥅', color: '#00bcd4', label: 'shot' },
  // Cool / neutral colours for the informational firehose.
  stat: { emoji: '📊', color: '#3f51b5', label: 'stat' },
  player_stat: { emoji: '👤', color: '#607d8b', label: 'player stat' },
  timer: { emoji: '⏱️', color: '#78909c', label: 'timer' },
  other: { emoji: '📌', color: '#9e9e9e', label: 'other' },
};

const FALLBACK: CategoryDisplayConfig = {
  emoji: '📌',
  color: '#9e9e9e',
  label: 'other',
};

/**
 * Returns the display config for a category, falling back to a neutral
 * grey pin for an unknown value — defensive against the backend rolling
 * out a new category before the frontend enum is updated.
 */
export function getCategoryDisplay(
  category: MatchEventCategory | string,
): CategoryDisplayConfig {
  const map = CATEGORY_DISPLAY as Record<string, CategoryDisplayConfig>;
  return map[category] ?? FALLBACK;
}
